/**
 * SpacesList Component
 *
 * Displays the hierarchical list of spaces, folders, lists, and items in the sidebar.
 */

import { useCallback, useState, useEffect, useRef } from 'react';
import { Layers, Star, Plus, FolderPlus, LayoutGrid, ChevronDown } from 'lucide-react';
import {
  DndContext,
  closestCenter,
  closestCorners,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from '@dnd-kit/sortable';
import type { Space, ViewMode, Folder, ListItem } from '@/types';
import type { SpaceItem } from '@/db/types';
import clsx from 'clsx';
import { SidebarItem } from '../SidebarItem';
import { CategoryItem } from '../CategoryItem';
import { SortableSpaceItem } from '../SortableSpaceItem';
import { SortableFolderItem } from '../SortableFolderItem';
import { SortableListItem } from '../SortableListItem';
import { SortableGenericItem } from '../SortableGenericItem';
import { updateOrder, moveSpace } from '@/db/projects';
import { useAppStore } from '@/store/appStore';
import { toast } from '@/utils/toast';
import { handleError } from '@/utils/errorHandler';
import { organizeProjectsFromDatabase, fetchCategoriesFromDatabase, organizeCategories } from '@/utils/organizeProjects';
import { clearStructureCache } from '@/utils/structureCache';
import { invalidateCache } from '@/utils/queryCache';
import { useOrganization } from '@/context/OrganizationContext';
import { useClickOutside } from '@/hooks/useClickOutside';
import type { SpacesListProps } from './types';
import { useContainerState } from './useContainerState';
import { FolderRenderer } from './FolderRenderer';

export function SpacesList({
  spaces,
  categories,
  filters,
  viewMode,
  totalTasks,
  loading,
  spaceItems,
  folderMappings,
  onOpenCreateSpace,
  onOpenCreateCategory,
  onFilterChange,
  onViewModeChange,
  onOpenContextMenu,
  onItemClick,
  isMobile,
  onClose,
}: SpacesListProps) {
  const [orderedSpaces, setOrderedSpaces] = useState(spaces);
  const [expandedSpaces, setExpandedSpaces] = useState<Set<string>>(() => {
    const initial = new Set<string>();
    if (filters.path) {
      // First segment of ID-based path is the space ID
      initial.add(filters.path.split('/')[0]);
    }
    return initial;
  });
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(() => {
    const initial = new Set<string>();
    if (filters.path) {
      const parts = filters.path.split('/');
      if (parts.length >= 2) {
        initial.add(`${parts[0]}/${parts[1]}`);
      }
    }
    return initial;
  });
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(() => {
    // Auto-expand the category containing the active project on initial render
    const initial = new Set<string>();
    if (filters.path && categories.length > 0) {
      // First segment of ID-based path is the space ID
      const spaceId = filters.path.split('/')[0];
      const cat = categories.find(c => c.projects.some(p => p.id === spaceId));
      if (cat) initial.add(cat.id);
    }
    return initial;
  });

  const { containerItems, folderDataMap, listDataMap, setContainerItems, setFolderDataMap, setListDataMap } =
    useContainerState(spaces, folderMappings, spaceItems);

  const currentItemType = useAppStore((state) => state.currentItemType);
  const currentDocument = useAppStore((state) => state.currentDocument);
  const currentChat = useAppStore((state) => state.currentChat);
  const currentVoiceChannel = useAppStore((state) => state.currentVoiceChannel);
  const currentSpreadsheet = useAppStore((state) => state.currentSpreadsheet);
  const currentPresentation = useAppStore((state) => state.currentPresentation);
  const setSpaces = useAppStore((state) => state.setSpaces);
  const setCategories = useAppStore((state) => state.setCategories);
  const editingItems = useAppStore((state) => state.editingItems);
  const projectItems = useAppStore((state) => state.projectItems);
  const setEditingItem = useAppStore((state) => state.setEditingItem);
  const setPendingItemCreation = useAppStore((state) => state.setPendingItemCreation);
  const { currentOrg } = useOrganization();
  const currentOrgId = currentOrg?.id;

  // Create menu state
  const [createMenuOpen, setCreateMenuOpen] = useState(false);
  const [createSubMenuOpen, setCreateSubMenuOpen] = useState(false);
  const createMenuRef = useRef<HTMLDivElement>(null);

  useClickOutside([createMenuRef], () => {
    setCreateMenuOpen(false);
    setCreateSubMenuOpen(false);
  }, createMenuOpen);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  // Sync expanded folders when temp items are added
  useEffect(() => {
    const pendingCreations = useAppStore.getState().pendingItemCreations;
    editingItems.forEach((itemId) => {
      if (!itemId.startsWith('temp-')) return;
      const creationInfo = pendingCreations[itemId];
      if (!creationInfo) return;

      const space = spaces.find((s) => s.name === creationInfo.spaceName);
      if (!space) return;

      space.folders.forEach((folder: Folder) => {
        if (folder.id && creationInfo.containerId === folder.id) {
          setExpandedFolders((prev) => {
            const key = `${space.id}/${folder.id}`;
            const newSet = new Set(prev);
            newSet.add(key);
            return newSet;
          });
        }
      });
    });
  }, [editingItems, spaces]);

  // Handle temp items in editing state
  useEffect(() => {
    if (editingItems.size === 0) return;

    editingItems.forEach((itemId) => {
      if (!itemId.startsWith('temp-')) return;

      for (const [spaceName, items] of Object.entries(projectItems)) {
        const tempItem = items.find((i) => i.id === itemId);
        if (tempItem) {
          const space = spaces.find((s) => s.name === spaceName);
          if (!space) continue;

          setExpandedSpaces((prev) => {
            const next = new Set(prev);
            next.add(space.id);
            return next;
          });

          setContainerItems((prev) => {
            const prefixedId = `item-${itemId}`;
            if (prev[space.id] && !prev[space.id].includes(prefixedId)) {
              return { ...prev, [space.id]: [...prev[space.id], prefixedId] };
            }
            return prev;
          });
          break;
        }
      }

      spaces.forEach((space) => {
        const tempFolder = space.folders.find((f: Folder) => f.id === itemId);
        if (tempFolder) {
          setExpandedSpaces((prev) => {
            const next = new Set(prev);
            next.add(space.id);
            return next;
          });

          setContainerItems((prev) => {
            const prefixedId = `folder-${itemId}`;
            if (prev[space.id] && !prev[space.id].includes(prefixedId)) {
              return { ...prev, [space.id]: [...prev[space.id], prefixedId] };
            }
            return prev;
          });

          setFolderDataMap((prev) => ({
            ...prev,
            [itemId]: { name: tempFolder.name, spaceId: space.id },
          }));
        }

        space.folders.forEach((folder: Folder) => {
          const folderId = folder.id;
          if (!folderId) return;
          const tempList = folder.lists?.find((l: ListItem) => l.id === itemId);
          if (tempList) {
            setExpandedSpaces((prev) => {
              const next = new Set(prev);
              next.add(space.id);
              return next;
            });

            setExpandedFolders((prev) => {
              const next = new Set(prev);
              next.add(`${space.id}/${folderId}`);
              return next;
            });

            setContainerItems((prev) => {
              const prefixedId = `list-${itemId}`;
              if (prev[folderId] && !prev[folderId].includes(prefixedId)) {
                return { ...prev, [folderId]: [...prev[folderId], prefixedId] };
              } else if (!prev[folderId]) {
                return { ...prev, [folderId]: [prefixedId] };
              }
              return prev;
            });

            setListDataMap((prev) => ({
              ...prev,
              [itemId]: { name: tempList.name, spaceId: space.id, folderId: folderId, taskCount: 0 },
            }));
          }
        });
      });
    });
  }, [editingItems, spaces, projectItems, setContainerItems, setFolderDataMap, setListDataMap]);

  useEffect(() => {
    setOrderedSpaces(spaces);
  }, [spaces]);

  const isSpaceEmpty = useCallback(
    (space: Space) => {
      const items = containerItems[space.id];
      return !items || items.length === 0;
    },
    [containerItems]
  );

  // Auto-expand removed: spaces start collapsed, user expands manually
  // Only auto-expand when navigating to a specific path (handled below)

  // Expand space/folder when item is selected
  useEffect(() => {
    if (!currentItemType) return;

    let currentItem: { parent_id?: string | null; id: string } | null = null;
    if (currentItemType === 'document' && currentDocument) currentItem = currentDocument;
    else if (currentItemType === 'chat' && currentChat) currentItem = currentChat;
    else if (currentItemType === 'voice_channel' && currentVoiceChannel) currentItem = currentVoiceChannel;
    else if (currentItemType === 'spreadsheet' && currentSpreadsheet) currentItem = currentSpreadsheet;
    else if (currentItemType === 'presentation' && currentPresentation) currentItem = currentPresentation;

    if (currentItem?.parent_id) {
      const spaceIdStr = String(currentItem.parent_id);
      const space = spaces.find((s) => s.id === spaceIdStr);
      // First segment of ID-based path is the space ID
      const spaceIdFromFilters = filters.path ? filters.path.split('/')[0] : null;

      if (space) {
        setExpandedSpaces((prev) => {
          const next = new Set(prev);
          next.add(space.id);
          return next;
        });

        const itemParentId = currentItem.parent_id;
        if (itemParentId && String(itemParentId) !== space.id) {
          const parentIdStr = String(itemParentId);
          // Find the folder by ID in folderDataMap
          const folderData = Object.entries(folderDataMap).find(([id]) => id === parentIdStr);

          if (folderData) {
            // Check if it's a root folder (directly under space)
            const spaceFolderMapping = folderMappings?.[space.name] || {};
            const isRootFolder = Object.values(spaceFolderMapping).some(id => String(id) === parentIdStr);

            if (isRootFolder) {
              setExpandedFolders((prev) => {
                const next = new Set(prev);
                next.add(`${space.id}/${parentIdStr}`);
                return next;
              });
            } else {
              setExpandedFolders((prev) => {
                const next = new Set(prev);
                next.add(`${space.id}/nested-${parentIdStr}`);
                return next;
              });
            }
          }
        }
      } else if (spaceIdFromFilters) {
        setExpandedSpaces((prev) => {
          const next = new Set(prev);
          next.add(spaceIdFromFilters);
          return next;
        });

        if (filters.path && filters.path.split('/').length > 1) {
          setExpandedFolders((prev) => {
            const next = new Set(prev);
            next.add(filters.path!);
            return next;
          });
        }
      }
    }
  }, [
    currentItemType,
    currentDocument,
    currentChat,
    currentVoiceChannel,
    currentSpreadsheet,
    currentPresentation,
    spaces,
    folderMappings,
    folderDataMap,
    filters.path,
  ]);

  useEffect(() => {
    if (!filters.path) return;
    // First segment is the space ID
    const spaceId = filters.path.split('/')[0];
    const space = spaces.find((s) => s.id === spaceId);

    if (space && spaceItems?.[space.name]) {
      setExpandedSpaces((prev) => {
        if (prev.has(spaceId)) return prev;
        const next = new Set(prev);
        next.add(spaceId);
        return next;
      });

      if (filters.path.split('/').length > 1) {
        const folderKey = `${filters.path.split('/')[0]}/${filters.path.split('/')[1]}`;
        setExpandedFolders((prev) => {
          if (prev.has(folderKey)) return prev;
          const next = new Set(prev);
          next.add(folderKey);
          return next;
        });
      }
    }
  }, [spaceItems, filters.path, spaces]);

  // Auto-expand category when path changes
  useEffect(() => {
    if (!filters.path || categories.length === 0) return;
    // First segment is the space ID
    const spaceId = filters.path.split('/')[0];
    const cat = categories.find(c => c.projects.some(p => p.id === spaceId));
    if (cat) {
      setExpandedCategories((prev) => {
        if (prev.has(cat.id)) return prev;
        const next = new Set(prev);
        next.add(cat.id);
        return next;
      });
    }
  }, [filters.path, categories]);

  const closeOnMobile = useCallback(() => {
    if (isMobile) onClose?.();
  }, [isMobile, onClose]);

  const isActive = useCallback((path: string | null) => filters.path === path, [filters]);
  const isAncestorOfActive = useCallback(
    (path: string) => !!filters.path && filters.path !== path && filters.path.startsWith(path + '/'),
    [filters],
  );

  const toggleSpace = useCallback((name: string) => {
    setExpandedSpaces((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  }, []);

  const toggleFolder = useCallback((key: string) => {
    setExpandedFolders((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const toggleCategory = useCallback((categoryId: string) => {
    setExpandedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(categoryId)) next.delete(categoryId);
      else next.add(categoryId);
      return next;
    });
  }, []);

  const handleSpaceClick = useCallback(
    (spaceId: string) => {
      onFilterChange({ path: spaceId });
      const space = spaces.find((s) => s.id === spaceId);
      if (space && containerItems[space.id]?.length > 0) {
        setExpandedSpaces((prev) => {
          const next = new Set(prev);
          next.add(spaceId);
          return next;
        });
      }
      closeOnMobile();
    },
    [onFilterChange, closeOnMobile, spaces, containerItems]
  );

  const handleFolderClick = useCallback(
    (spaceId: string, folderId: string) => {
      onFilterChange({ path: `${spaceId}/${folderId}` });
      closeOnMobile();
    },
    [onFilterChange, closeOnMobile]
  );

  const handleListClick = useCallback(
    (spaceId: string, folderId: string | null, listId: string) => {
      const path = folderId ? `${spaceId}/${folderId}/${listId}` : `${spaceId}/${listId}`;
      onFilterChange({ path });
      closeOnMobile();
    },
    [onFilterChange, closeOnMobile]
  );

  const handleAllTasks = useCallback(() => {
    onFilterChange({ path: null });
    closeOnMobile();
  }, [onFilterChange, closeOnMobile]);

  const handleViewModeChange = useCallback(
    (mode: ViewMode) => {
      onViewModeChange(mode);
      closeOnMobile();
    },
    [onViewModeChange, closeOnMobile]
  );

  // Helper to refresh both spaces and categories from DB
  const refreshSpacesAndCategories = useCallback(async () => {
    // Clear both IndexedDB and in-memory query cache so fresh order is fetched
    invalidateCache('getAllLists');
    invalidateCache('getAllFolders');
    invalidateCache('getAllSpaces');
    await clearStructureCache();
    const [refreshedSpaces, dbCategories] = await Promise.all([
      organizeProjectsFromDatabase(undefined, { forceRefresh: true, orgId: currentOrgId }),
      fetchCategoriesFromDatabase(currentOrgId),
    ]);
    setSpaces(refreshedSpaces);
    if (dbCategories.length > 0) {
      setCategories(organizeCategories(dbCategories, refreshedSpaces));
    }
  }, [currentOrgId, setSpaces, setCategories]);

  const handleDragEnd = useCallback(
    async (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;

      const activeIdStr = String(active.id);
      const overIdStr = String(over.id);

      const activePrefix = activeIdStr.split('-')[0];
      const overPrefix = overIdStr.split('-')[0];
      const activeRealId = activeIdStr.substring(activePrefix.length + 1);
      const overRealId = overIdStr.substring(overPrefix.length + 1);

      if (overPrefix === 'folder') {
        try {
          await moveSpace(activeRealId, overRealId);
          await refreshSpacesAndCategories();

          let targetFolderKey: string | null = null;
          for (const space of spaces) {
            const rootFolderName = Object.entries(folderMappings?.[space.name] || {}).find(
              ([, id]) => id === overRealId
            )?.[0];
            if (rootFolderName) {
              targetFolderKey = `${space.id}/${overRealId}`;
              break;
            }

            const folderData = Object.entries(folderDataMap).find(([id]) => id === overRealId);
            if (folderData) {
              const [folderId, data] = folderData;
              const containingSpace = spaces.find((s) => s.id === data.spaceId);
              if (containingSpace) {
                const isRootFolder = Object.values(folderMappings?.[containingSpace.name] || {}).some(
                  id => id === folderId
                );
                targetFolderKey = isRootFolder
                  ? `${containingSpace.id}/${folderId}`
                  : `${containingSpace.id}/nested-${folderId}`;
                break;
              }
            }
          }

          if (targetFolderKey) {
            setExpandedFolders((prev) => {
              const next = new Set(prev);
              next.add(targetFolderKey!);
              return next;
            });
          }
        } catch (error) {
          handleError(error, { level: 'error', message: 'Errore nello spostamento dell\'elemento nella cartella' });
          try {
            await refreshSpacesAndCategories();
          } catch (refreshError) {
            handleError(refreshError, { level: 'warning', message: 'Errore nell\'aggiornamento della struttura dopo lo spostamento fallito' });
          }
          toast(`Errore nello spostamento: ${error instanceof Error ? error.message : 'Errore sconosciuto'}`, 'error');
        }
        return;
      }

      let containerId: string | null = null;
      for (const [cid, items] of Object.entries(containerItems)) {
        if (items.includes(activeIdStr) && items.includes(overIdStr)) {
          containerId = cid;
          break;
        }
      }

      if (!containerId) return;

      const items = containerItems[containerId];
      const oldIndex = items.indexOf(activeIdStr);
      const newIndex = items.indexOf(overIdStr);

      if (oldIndex === -1 || newIndex === -1) return;

      const newItems = arrayMove(items, oldIndex, newIndex);
      setContainerItems((prev) => ({ ...prev, [containerId!]: newItems }));

      const updates = newItems.map((prefixedId, index) => {
        const prefix = prefixedId.split('-')[0];
        const realId = prefixedId.substring(prefix.length + 1);
        return { id: realId, orderIndex: index };
      });

      try {
        await updateOrder(updates);
        // Update Zustand store so the new order persists on the next page refresh
        refreshSpacesAndCategories().catch(() => {});
      } catch (error) {
        handleError(error, { level: 'error', message: 'Errore nell\'aggiornamento dell\'ordine degli elementi' });
        setContainerItems((prev) => ({ ...prev, [containerId!]: items }));
        toast(`Errore nel riordinamento: ${error instanceof Error ? error.message : 'Errore sconosciuto'}`, 'error');
      }
    },
    [containerItems, spaces, folderMappings, folderDataMap, setContainerItems, refreshSpacesAndCategories]
  );

  const handleSpacesDragEnd = useCallback(
    async (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;

      const oldIndex = orderedSpaces.findIndex((s) => s.id === active.id);
      const newIndex = orderedSpaces.findIndex((s) => s.id === over.id);
      if (oldIndex === -1 || newIndex === -1) return;

      const newOrderedSpaces = arrayMove(orderedSpaces, oldIndex, newIndex);
      setOrderedSpaces(newOrderedSpaces);

      const updates = newOrderedSpaces.map((space, index) => ({ id: space.id, orderIndex: index }));

      try {
        await updateOrder(updates);
      } catch (error) {
        handleError(error, { level: 'error', message: 'Errore nell\'aggiornamento dell\'ordine dei progetti' });
        setOrderedSpaces(orderedSpaces);
        toast(
          `Errore nel riordinamento progetti: ${error instanceof Error ? error.message : 'Errore sconosciuto'}`,
          'error'
        );
      }
    },
    [orderedSpaces]
  );

  // DnD handler for cross-category moves, within-category reordering, and category reordering
  const handleCategoryDragEnd = useCallback(
    async (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;

      const activeId = String(active.id);
      const overId = String(over.id);

      // Case 0: Category reorder (sortable-cat-* IDs)
      if (activeId.startsWith('sortable-cat-')) {
        const activeCatId = activeId.replace('sortable-cat-', '');
        const overCatId = overId.startsWith('sortable-cat-')
          ? overId.replace('sortable-cat-', '')
          : overId.startsWith('category-drop-')
            ? overId.replace('category-drop-', '')
            : null;
        if (!overCatId) return;

        const oldIdx = categories.findIndex(c => c.id === activeCatId);
        const newIdx = categories.findIndex(c => c.id === overCatId);
        if (oldIdx === -1 || newIdx === -1) return;

        const reordered = arrayMove(categories, oldIdx, newIdx);
        setCategories(reordered);

        // Persist category order
        const updates = reordered
          .filter(c => c.id !== 'uncategorized' && !c.id.startsWith('temp-'))
          .map((c, i) => ({ id: c.id, orderIndex: i }));
        try {
          await updateOrder(updates);
        } catch (error) {
          handleError(error, { level: 'error', message: 'Errore nel riordinamento delle categorie' });
          setCategories(categories);
          toast(`Errore: ${error instanceof Error ? error.message : 'Errore sconosciuto'}`, 'error');
        }
        return;
      }

      // Find which category the dragged project belongs to
      const activeCategory = categories.find(c => c.projects.some(p => p.id === activeId));
      if (!activeCategory) return;

      // Case 1: Dropped on a category header (droppable zone)
      if (overId.startsWith('category-drop-')) {
        const targetCategoryId = overId.replace('category-drop-', '');
        if (activeCategory.id === targetCategoryId) return; // Same category, no-op

        try {
          await moveSpace(activeId, targetCategoryId);
          await refreshSpacesAndCategories();
          // Auto-expand the target category
          setExpandedCategories(prev => {
            const next = new Set(prev);
            next.add(targetCategoryId);
            return next;
          });
        } catch (error) {
          handleError(error, { level: 'error', message: 'Errore nello spostamento del progetto' });
          toast(`Errore: ${error instanceof Error ? error.message : 'Errore sconosciuto'}`, 'error');
        }
        return;
      }

      // Case 2: Dropped on another space
      const overCategory = categories.find(c => c.projects.some(p => p.id === overId));
      if (!overCategory) return;

      if (activeCategory.id === overCategory.id) {
        // Same category - reorder within category
        const categorySpaces = activeCategory.projects;
        const oldIndex = categorySpaces.findIndex(s => s.id === activeId);
        const newIndex = categorySpaces.findIndex(s => s.id === overId);
        if (oldIndex === -1 || newIndex === -1) return;

        const newOrder = arrayMove(categorySpaces, oldIndex, newIndex);

        // Optimistic update
        const updatedCategories = categories.map(c => {
          if (c.id !== activeCategory.id) return c;
          return { ...c, projects: newOrder };
        });
        setCategories(updatedCategories);

        // Persist order
        const updates = newOrder.map((space, index) => ({ id: space.id, orderIndex: index }));
        try {
          await updateOrder(updates);
        } catch (error) {
          handleError(error, { level: 'error', message: 'Errore nel riordinamento dei progetti' });
          setCategories(categories);
          toast(`Errore: ${error instanceof Error ? error.message : 'Errore sconosciuto'}`, 'error');
        }
      } else {
        // Different category - move to target category
        try {
          await moveSpace(activeId, overCategory.id);
          await refreshSpacesAndCategories();
          // Auto-expand the target category
          setExpandedCategories(prev => {
            const next = new Set(prev);
            next.add(overCategory.id);
            return next;
          });
        } catch (error) {
          handleError(error, { level: 'error', message: 'Errore nello spostamento del progetto' });
          toast(`Errore: ${error instanceof Error ? error.message : 'Errore sconosciuto'}`, 'error');
        }
      }
    },
    [categories, refreshSpacesAndCategories, setCategories]
  );

  const quickStats = { total: 0, spaces: spaces.length };

  // Render a single space item (reused in both flat and categorized views)
  const renderSpaceItem = (space: Space) => {
    const isEmpty = isSpaceEmpty(space);
    const spaceIsExpanded = expandedSpaces.has(space.id);

    return (
      <SortableSpaceItem
        key={space.id}
        space={space}
        isEmpty={isEmpty}
        isActive={isActive(space.id)}
        isAncestorActive={isAncestorOfActive(space.id)}
        isExpanded={spaceIsExpanded}
        onToggle={() => toggleSpace(space.id)}
        onClick={() => {
          handleSpaceClick(space.id);
          handleViewModeChange('list');
        }}
        onContextMenu={(e) => onOpenContextMenu(e, 'space', space.name)}
      >
        {spaceIsExpanded && containerItems[space.id] && (
          <DndContext sensors={sensors} collisionDetection={closestCorners} onDragEnd={handleDragEnd}>
            <SortableContext items={containerItems[space.id]} strategy={verticalListSortingStrategy}>
              <div className="ml-4 pl-2 border-l border-border-primary/50 mt-0.5 space-y-0.5">
                {containerItems[space.id].map((prefixedId) => {
                  const [prefix, ...idParts] = prefixedId.split('-');
                  const itemId = idParts.join('-');

                  if (prefix === 'folder') {
                    const folderData = folderDataMap[itemId];
                    if (!folderData) return null;

                    const folderKey = `${space.id}/${itemId}`;
                    const folderIsExpanded = expandedFolders.has(folderKey);

                    const folder = {
                      id: itemId,
                      name: folderData.name,
                      lists: [],
                      taskCount: 0,
                      type: 'folder' as const,
                    };

                    const isEditingThis = editingItems.has(itemId);

                    return (
                      <div key={prefixedId}>
                        <SortableFolderItem
                          folder={folder}
                          folderId={itemId}
                          spaceName={space.name}
                          folderKey={folderKey}
                          isActive={isActive(`${space.id}/${itemId}`)}
                          isAncestorActive={isAncestorOfActive(`${space.id}/${itemId}`)}
                          isExpanded={folderIsExpanded}
                          onToggle={() => toggleFolder(folderKey)}
                          onClick={() => {
                            handleFolderClick(space.id, itemId);
                            handleViewModeChange('list');
                          }}
                          onContextMenu={(e) => onOpenContextMenu(e, 'folder', space.name, folder.name)}
                          isEditing={isEditingThis}
                          onCancel={() => {
                            setEditingItem(itemId, false);
                            setPendingItemCreation(itemId, null);
                            const currentSpaces = useAppStore.getState().spaces;
                            const updatedSpaces = currentSpaces.map((s) => {
                              if (s.name !== space.name) return s;
                              return { ...s, folders: s.folders.filter((f: Folder) => f.id !== itemId) };
                            });
                            useAppStore.getState().setSpaces(updatedSpaces);
                          }}
                        >
                          {folderIsExpanded && containerItems[itemId] && (
                            <DndContext
                              sensors={sensors}
                              collisionDetection={closestCorners}
                              onDragEnd={handleDragEnd}
                            >
                              <SortableContext
                                items={containerItems[itemId]}
                                strategy={verticalListSortingStrategy}
                              >
                                <div className="ml-4 pl-2 border-l border-border-primary/30 mt-0.5 space-y-0.5">
                                  <FolderRenderer
                                    containerId={itemId}
                                    space={space}
                                    containerItems={containerItems}
                                    folderDataMap={folderDataMap}
                                    listDataMap={listDataMap}
                                    expandedFolders={expandedFolders}
                                    spaceItems={spaceItems}
                                    currentItemType={currentItemType}
                                    currentDocument={currentDocument}
                                    currentChat={currentChat}
                                    currentVoiceChannel={currentVoiceChannel}
                                    currentSpreadsheet={currentSpreadsheet}
                                    currentPresentation={currentPresentation}
                                    isActive={isActive}
                                    toggleFolder={toggleFolder}
                                    handleFolderClick={handleFolderClick}
                                    handleListClick={handleListClick}
                                    handleViewModeChange={handleViewModeChange}
                                    onOpenContextMenu={onOpenContextMenu}
                                    onItemClick={onItemClick}
                                    onDragEnd={handleDragEnd}
                                  />
                                </div>
                              </SortableContext>
                            </DndContext>
                          )}
                        </SortableFolderItem>
                      </div>
                    );
                  } else if (prefix === 'list') {
                    const listData = listDataMap[itemId];
                    if (!listData) return null;

                    const list = { id: itemId, name: listData.name, taskCount: listData.taskCount };
                    const folderName = listData.folderId ? folderDataMap[listData.folderId]?.name || null : null;
                    const folderId = listData.folderId || null;
                    const isEditingThis = editingItems.has(itemId);

                    return (
                      <SortableListItem
                        key={prefixedId}
                        list={list}
                        listId={itemId}
                        spaceName={space.name}
                        folderName={folderName}
                        isActive={isActive(
                          folderId
                            ? `${space.id}/${folderId}/${itemId}`
                            : `${space.id}/${itemId}`
                        )}
                        onClick={() => {
                          handleListClick(space.id, folderId, itemId);
                          handleViewModeChange('list');
                        }}
                        onContextMenu={(e) =>
                          onOpenContextMenu(e, 'list', space.name, folderName || undefined, list.name)
                        }
                        isEditing={isEditingThis}
                        onCancel={() => {
                          setEditingItem(itemId, false);
                          setPendingItemCreation(itemId, null);
                          const currentSpaces = useAppStore.getState().spaces;
                          const updatedSpaces = currentSpaces.map((s) => {
                            if (s.name !== space.name) return s;
                            return {
                              ...s,
                              folders: s.folders.map((f: Folder) => ({
                                ...f,
                                lists: (f.lists || []).filter((l: ListItem) => l.id !== itemId),
                              })),
                            };
                          });
                          useAppStore.getState().setSpaces(updatedSpaces);
                        }}
                      />
                    );
                  } else if (prefix === 'item') {
                    const items = (spaceItems?.[space.name] || []) as SpaceItem[];
                    const item = items.find((i: SpaceItem) => i.id === itemId);
                    if (!item?.type) return null;

                    const itemType = item.type as
                      | 'document'
                      | 'chat'
                      | 'voice_channel'
                      | 'spreadsheet'
                      | 'presentation';
                    const isCurrent =
                      currentItemType === itemType &&
                      ((itemType === 'document' && currentDocument?.id === item.id) ||
                        (itemType === 'chat' && currentChat?.id === item.id) ||
                        (itemType === 'voice_channel' && currentVoiceChannel?.id === item.id) ||
                        (itemType === 'spreadsheet' && currentSpreadsheet?.id === item.id) ||
                        (itemType === 'presentation' && currentPresentation?.id === item.id));

                    return (
                      <SortableGenericItem
                        key={prefixedId}
                        item={{ ...item, type: itemType }}
                        isActive={isCurrent}
                        onClick={() => onItemClick?.(itemType, item.id)}
                        onContextMenu={(e) => onOpenContextMenu(e, 'item', space.name, undefined, undefined, item.id, itemType, item.name)}
                      />
                    );
                  }

                  return null;
                })}
              </div>
            </SortableContext>
          </DndContext>
        )}
      </SortableSpaceItem>
    );
  };

  // Determine if we should use categorized view
  const hasCategories = categories.length > 0;

  return (
    <>
      <div className="group relative" ref={createMenuRef}>
        <SidebarItem
          icon={<Layers className="w-4 h-4" />}
          label="Progetti"
          count={quickStats.spaces}
          reserveCountSpace={true}
          isActive={viewMode === 'projects'}
          onClick={() => handleViewModeChange('projects')}
          color="violet"
        />
        <button
          onClick={(e) => {
            e.stopPropagation();
            if (categories.length > 0) {
              setCreateMenuOpen((prev) => !prev);
              setCreateSubMenuOpen(false);
            } else {
              onOpenCreateSpace?.();
            }
          }}
          className={clsx(
            'absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-md transition-all',
            'bg-accent-violet text-white hover:bg-accent-violet/90',
            'opacity-0 group-hover:opacity-100 md:opacity-100'
          )}
          title="Crea progetto o categoria"
        >
          <Plus className="w-3 h-3" />
        </button>

        {/* Create dropdown menu */}
        {createMenuOpen && (
          <div className="absolute right-0 top-full mt-1 z-50 w-48 bg-surface-primary border border-border-primary rounded-lg shadow-lg py-1 animate-fade-in">
            {/* New Project option */}
            <div>
              <button
                onClick={() => {
                  const realCategories = categories.filter(c => !c.id.startsWith('temp-') && c.id !== 'uncategorized');
                  if (realCategories.length === 0) {
                    onOpenCreateSpace?.();
                    setCreateMenuOpen(false);
                  } else {
                    setCreateSubMenuOpen(prev => !prev);
                  }
                }}
                className="w-full flex items-center gap-2 px-3 py-2 text-xs text-text-secondary hover:bg-surface-hover transition-colors"
              >
                <FolderPlus className="w-3.5 h-3.5 text-text-tertiary" />
                <span>Nuovo Progetto</span>
                {categories.filter(c => !c.id.startsWith('temp-') && c.id !== 'uncategorized').length > 0 && (
                  <ChevronDown className={clsx('ml-auto w-3 h-3 text-text-quaternary transition-transform', createSubMenuOpen && 'rotate-180')} />
                )}
              </button>

              {/* Category list inline (no flyout to avoid sidebar overflow clipping) */}
              {createSubMenuOpen && categories.filter(c => !c.id.startsWith('temp-') && c.id !== 'uncategorized').length > 0 && (
                <div className="border-t border-border-primary/50">
                  {categories
                    .filter(c => !c.id.startsWith('temp-') && c.id !== 'uncategorized')
                    .map((cat) => (
                      <button
                        key={cat.id}
                        onClick={() => {
                          onOpenCreateSpace?.(cat.id);
                          setCreateMenuOpen(false);
                          setCreateSubMenuOpen(false);
                        }}
                        className="w-full flex items-center gap-2 pl-8 pr-3 py-1.5 text-xs text-text-secondary hover:bg-surface-hover transition-colors"
                      >
                        <span className="truncate">{cat.name}</span>
                      </button>
                    ))}
                  <button
                    onClick={() => {
                      onOpenCreateSpace?.();
                      setCreateMenuOpen(false);
                      setCreateSubMenuOpen(false);
                    }}
                    className="w-full flex items-center gap-2 pl-8 pr-3 py-1.5 text-xs text-text-quaternary hover:bg-surface-hover transition-colors"
                  >
                    <span>Senza categoria</span>
                  </button>
                </div>
              )}
            </div>

            {/* New Category option */}
            <button
              onClick={() => {
                onOpenCreateCategory?.();
                setCreateMenuOpen(false);
                setCreateSubMenuOpen(false);
              }}
              className="w-full flex items-center gap-2 px-3 py-2 text-xs text-text-secondary hover:bg-surface-hover transition-colors"
            >
              <LayoutGrid className="w-3.5 h-3.5 text-text-tertiary" />
              <span>Nuova Categoria</span>
            </button>
          </div>
        )}
      </div>

      <div className="mt-1 mb-3">
        <SidebarItem
          icon={<Star className="w-4 h-4" />}
          label="Tutti i Task"
          count={totalTasks}
          isLoading={loading}
          reserveCountSpace={true}
          isActive={isActive(null) && viewMode === 'list'}
          onClick={() => {
            handleAllTasks();
            handleViewModeChange('list');
          }}
        />
      </div>

      {hasCategories ? (
        /* Categorized view: single DndContext for cross-category moves and category reordering */
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleCategoryDragEnd}>
          <SortableContext
            items={categories.map((c) => `sortable-cat-${c.id}`)}
            strategy={verticalListSortingStrategy}
          >
          <div className="space-y-1 pb-4">
            {categories.map((category) => {
              const categoryIsExpanded = expandedCategories.has(category.id);
              const categorySpaces = category.projects;

              return (
                <CategoryItem
                  key={category.id}
                  category={category}
                  isExpanded={categoryIsExpanded}
                  onToggle={() => toggleCategory(category.id)}
                  onCancelCreate={() => {
                    setEditingItem(category.id, false);
                    setPendingItemCreation(category.id, null);
                    const currentCategories = useAppStore.getState().categories;
                    useAppStore.getState().setCategories(currentCategories.filter(c => c.id !== category.id));
                  }}
                >
                  <SortableContext
                    items={categorySpaces.map((s) => s.id)}
                    strategy={verticalListSortingStrategy}
                  >
                    <div className="space-y-0.5">
                      {categorySpaces.map((space) => renderSpaceItem(space))}
                    </div>
                  </SortableContext>
                </CategoryItem>
              );
            })}
          </div>
          </SortableContext>
        </DndContext>
      ) : (
        /* Flat view: all projects without categories (fallback) */
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleSpacesDragEnd}>
          <SortableContext items={orderedSpaces.map((s) => s.id)} strategy={verticalListSortingStrategy}>
            <div className="space-y-0.5 pb-4">
              {orderedSpaces.map((space) => renderSpaceItem(space))}
            </div>
          </SortableContext>
        </DndContext>
      )}
    </>
  );
}
